import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import {
  ConfirmationModal,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
  useToasts,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  Tag,
  Text,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useTimeOffHook from "@/hooks/use-time-off.hook.tsx";
import {
  useTimeOffRequestCancel,
  useTimeOffRequestDelete,
} from "@/queries/use-time-off-requests.query.ts";
import { useTimeOffDeleteAdjustment } from "@/queries/use-time-off.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { getRequestStatusTitle } from "@/types/approvals/approval.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiTimeOffLogFull, ApiTimeOffLogType } from "@/types/time-off/time-off.types.ts";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface Props {
  logs: ApiTimeOffLogFull[];
  isLoading: boolean;
}

export const TimeOffHistoryReportTable = ({ logs, isLoading }: Props) => {
  const { t } = useTranslation();
  const { renderDate, renderEmployeeName } = useFormatter();
  const { getTimeOffRequestStatusColor, getActionTypeTagTitle, getActionTypeTag } =
    useTimeOffHook();
  const { openViewRequest, openEditBalance } = useTimeOffHook();
  const { showInfo } = useToasts();
  const { openModal } = useModals();
  const deleteTimeOffRequest = useTimeOffRequestDelete();
  const cancelRequest = useTimeOffRequestCancel();
  const deleteAdjustment = useTimeOffDeleteAdjustment();
  const [isViewButtonLoadingId, setIsViewButtonLoading] = useState<number | undefined>(undefined);

  const getStatusTag = (entity: ApiTimeOffLogFull) => {
    if (!entity.approval_request_details) {
      const settings = getActionTypeTag(entity.time_off_log.action);

      return (
        <Stack>
          {entity.time_off_log.created_by ? (
            <Tooltip>
              <TooltipTrigger>
                <Tag variant={settings.tagVariant} appearance={settings.tagAppearance}>
                  <Stack
                    direction={"horizontal"}
                    items={"center"}
                    gap={"xs"}
                    className={"-my-[1px]"}
                  >
                    <EmployeeAvatar employee={entity.time_off_log.created_by} size={"xs"} />
                    {getActionTypeTagTitle(entity.time_off_log.action)}
                  </Stack>
                </Tag>
              </TooltipTrigger>
              <TooltipContent>
                {entity.time_off_log.action === ApiTimeOffLogType.adjustment
                  ? t("time_off:adjusted_by", {
                      name: renderEmployeeName(entity.time_off_log.created_by),
                    })
                  : renderEmployeeName(entity.time_off_log.created_by)}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tag variant={settings.tagVariant} appearance={settings.tagAppearance}>
              {getActionTypeTagTitle(entity.time_off_log.action)}
            </Tag>
          )}
        </Stack>
      );
    }

    const settings = getTimeOffRequestStatusColor(entity.approval_request_details.status);

    return (
      <Tag variant={settings.tagVariant} appearance={settings.tagAppearance}>
        {getRequestStatusTitle(entity.approval_request_details.status)}
      </Tag>
    );
  };

  const handleCancelRequest = (request: ApiTimeOffLogFull) => {
    if (!request.approval_request_details?.id) return;
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("approval:cancel_request_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          if (!request.approval_request_details) return;
          await cancelRequest.mutateAsync(request?.approval_request_details.id);

          analyticsService.trackEvent(analyticEvents.approvals.requestAction, {
            [analyticProperties.id]: request.approval_request_details?.id,
            [analyticProperties.title]: "Time Off",
            [analyticProperties.actionType]: "Cancel",
          });
        } catch {
          return;
        }
      },
    });
  };

  const handleDeleteRequest = (request: ApiTimeOffLogFull) => {
    if (!request.time_off_log.id && !request.approval_request_details?.id) {
      return;
    }

    openModal(ConfirmationModal, {
      question: request.approval_request_details?.id
        ? t("common:attention")
        : t("time_off:delete_adjustment_confirm"),
      text: request.approval_request_details?.id
        ? t("core:delete_request_confirm_description")
        : t("time_off:delete_adjustment_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          if (request.approval_request_details) {
            await deleteTimeOffRequest.mutateAsync(request.approval_request_details.id);
          } else if (request.time_off_log.id) {
            await deleteAdjustment.mutateAsync(request.time_off_log.id);
          } else {
            return;
          }
          showInfo({
            title: request.approval_request_details?.id
              ? t("approval:request_deleted")
              : t("time_off:adjustment_deleted"),
          });
          analyticsService.trackEvent(analyticEvents.approvals.requestAction, {
            [analyticProperties.id]: request.approval_request_details?.id,
            [analyticProperties.title]: "Time Off",
            [analyticProperties.actionType]: "Delete",
          });
        } catch {
          return;
        }
      },
    });
  };

  const columnHelper = createColumnHelper<ApiTimeOffLogFull>();
  const columns = useMemo<ColumnDef<ApiTimeOffLogFull>[]>(() => {
    const inner: DisplayColumnDef<ApiTimeOffLogFull, unknown>[] = [];

    inner.push(
      columnHelper.display({
        id: "created",
        header: t("common:date"),
        cell: (cell) => (
          <Tooltip>
            <TooltipTrigger>
              <Text className={"text-secondary"}>
                {renderDate(cell.row.original.time_off_log.date_adjustment)}
              </Text>
            </TooltipTrigger>
            <TooltipContent>
              {t("common:created")}: {renderDate(cell.row.original.time_off_log.date_created)}
            </TooltipContent>
          </Tooltip>
        ),
        ...ColumnSizes.date,
      })
    );

    inner.push(
      columnHelper.display({
        id: "full_name",
        header: t("core:full_name"),
        cell: (cell) => (
          <Stack className={"overflow-hidden text-ellipsis whitespace-nowrap"}>
            <EmployeeItem
              enablePreview
              employee={cell.row.original.time_off_log.employee}
              direction="horizontal"
              showPosition={false}
              truncate
            />
          </Stack>
        ),
        ...ColumnSizes.standard,
      })
    );

    inner.push(
      columnHelper.display({
        id: "category",
        header: t("time_off:category"),
        cell: (cell) => (
          <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
            <Avatar emoji={cell.row.original.time_off_log.category.emoji} size={"sm"} />
            <Text>{cell.row.original.time_off_log.category.title}</Text>
          </Stack>
        ),
        ...ColumnSizes.standard,
      })
    );

    inner.push(
      columnHelper.display({
        id: "status",
        header: t("common:status"),
        cell: (cell) => <Stack items={"start"}>{getStatusTag(cell.row.original)}</Stack>,
        ...ColumnSizes.status,
      })
    );

    inner.push(
      columnHelper.display({
        id: "balance",
        header: t("time_off:balance_change"),
        cell: (cell) => (
          <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
            <Text>{cell.row.original.time_off_log.balance}</Text>
            {cell.row.original.time_off_log.is_accrual_capped && (
              <Tooltip>
                <TooltipTrigger>
                  <FontAwesomeIcon icon={"fa-sharp fa-light fa-circle-info"} />
                </TooltipTrigger>
                <TooltipContent>{t("time_off:accrual_capped_note")}</TooltipContent>
              </Tooltip>
            )}
          </Stack>
        ),
        ...ColumnSizes.status,
      })
    );

    inner.push(
      columnHelper.display({
        id: "balance_after",
        header: t("time_off:balance_after"),
        cell: (cell) => <Text>{cell.row.original.time_off_log.balance_after ?? "—"}</Text>,
        ...ColumnSizes.status,
      })
    );

    inner.push(
      columnHelper.display({
        id: "period",
        header: t("common:period"),
        cell: (cell) => {
          if (cell.row.original.time_off_log.action == ApiTimeOffLogType.adjustment) {
            return null;
          }
          return (
            <>
              {cell.row.original.time_off_log.date_from &&
                cell.row.original.time_off_log.date_to && (
                  <Text>
                    {renderDate(new Date(cell.row.original.time_off_log.date_from))} —{" "}
                    {renderDate(new Date(cell.row.original.time_off_log.date_to))}
                  </Text>
                )}
            </>
          );
        },
        ...ColumnSizes.standard,
      })
    );

    inner.push(
      columnHelper.display({
        id: "comment",
        header: t("common:comment"),
        cell: (cell) =>
          cell.row.original.time_off_log.comment ? (
            <Stack justify={"center"} items={"center"}>
              <Tooltip>
                <TooltipTrigger>
                  <FontAwesomeIcon icon={"fa-light fa-comment"} />
                </TooltipTrigger>
                <TooltipContent>{cell.row.original.time_off_log.comment}</TooltipContent>
              </Tooltip>
            </Stack>
          ) : (
            ""
          ),
        ...ColumnSizes.tiny,
      })
    );

    inner.push({
      id: "actions",
      cell: (data) => {
        const request = data.row.original;

        const canEditAdjustment =
          request.acl.can_edit && request.time_off_log.action === ApiTimeOffLogType.adjustment;

        if (
          !request.acl.can_view &&
          !request.acl.can_cancel &&
          !request.acl.can_delete &&
          !canEditAdjustment
        ) {
          return null;
        }

        return (
          <Stack className={"justify-end"} gap={"sm"} direction={"horizontal"}>
            {request.acl.can_view && request.approval_request_details && (
              <Button
                isLoading={isViewButtonLoadingId == request.time_off_log.id}
                onClick={async () => {
                  if (request.approval_request_details) {
                    setIsViewButtonLoading(request.time_off_log.id);
                    await openViewRequest(request.approval_request_details.id);
                    setIsViewButtonLoading(undefined);
                  }
                }}
                variant={"secondary"}
              >
                {t("common:view")}
              </Button>
            )}
            {(canEditAdjustment || request.acl.can_cancel || request.acl.can_delete) && (
              <DropdownMenu>
                {canEditAdjustment && (
                  <DropdownMenuItem
                    onClick={() => {
                      openEditBalance(request.time_off_log.id);
                    }}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-edit"} />}
                    title={t("common:edit")}
                  />
                )}
                {request.acl.can_cancel && (
                  <DropdownMenuItem
                    onClick={() => {
                      handleCancelRequest(request);
                    }}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-xmark"} />}
                    title={t("common:cancel")}
                  />
                )}
                {request.acl.can_delete && (
                  <DropdownMenuItem
                    onClick={() => {
                      handleDeleteRequest(request);
                    }}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                    variant={"menuDanger"}
                    title={t("common:delete")}
                  />
                )}
              </DropdownMenu>
            )}
          </Stack>
        );
      },
      ...ColumnSizes.actions,
    });

    return inner;
  }, [logs, isViewButtonLoadingId]);

  return (
    <Table
      data={logs}
      columns={columns}
      showSkeleton={isLoading}
      stickyHeader={true}
      stickyFirstColumn={true}
      stickyLastColumn={true}
    />
  );
};
